import { authNativeHandoffCodeCreate, type CodeChallengeMethodEnum } from "@sidegigllc/api/client";
import { safeSessionStorage } from "utils/safeStorage";

// System-browser native handoff: the app opens login in a dedicated auth session (iOS ASWebAuthenticationSession /
// Android Custom Tabs), so on success we mint a one-time code and redirect to the app's callback scheme. The auth
// session captures that redirect privately (not OS scheme-dispatch); PKCE + a launch nonce carry the security — see the docs.

// Query params on /auth: the app's return URL, the PKCE challenge it must later prove it owns, and the
// server-issued launch nonce the mint verifies (a link without one can't drive a genuine app launch).
const NATIVE_REDIRECT_PARAM = "native_redirect";
const CODE_CHALLENGE_PARAM = "code_challenge";
const CODE_CHALLENGE_METHOD_PARAM = "code_challenge_method";
const LAUNCH_NONCE_PARAM = "launch";

// Persisted in sessionStorage like the account-linking code, so it survives the login navigation, the
// SSO full-page round-trip, and the signup wizard; every access is wrapped against a blocked-storage throw.
const STORAGE_KEY = "qwick.nativeHandoff";

// The business app's auth-session callback scheme (iOS + Android). The auth session captures this redirect
// privately, so it isn't the OS-dispatch hijack vector; the exact match is the open-redirect guard. Not https:
// ASWebAuthenticationSession matches by callbackURLScheme, and https there needs iOS 17.4+ (the app targets 16.4).
const ALLOWED_CALLBACKS: ReadonlyArray<{ protocol: string; host: string; path: string }> = [
  { protocol: "qwickbusinessapp:", host: "native-auth-callback", path: "" },
];
// PKCE S256 challenge: 43 url-safe base64 chars (SHA-256 → 32 bytes, unpadded). Only S256 is accepted.
const CODE_CHALLENGE_RE = /^[A-Za-z0-9_-]{43}$/;

/** A validated native-handoff target; `challenge`/`launchNonce` present mean the app supplied a usable PKCE challenge / launch nonce. */
export type NativeHandoff = { redirect: string; challenge?: string; method?: "S256"; launchNonce?: string };

/** Whether `candidate` exactly matches an allowlisted app callback (scheme + host + path); the open-redirect guard. */
export function isAllowedCallbackUrl(candidate: string): boolean {
  let url: URL;
  try {
    url = new URL(candidate);
  } catch {
    return false;
  }
  // URL lowercases the scheme but keeps a custom scheme's host case (opaque host), so lowercase it here; the path
  // stays an exact match.
  return ALLOWED_CALLBACKS.some(
    (cb) => url.protocol === cb.protocol && url.host.toLowerCase() === cb.host && url.pathname === cb.path,
  );
}

/** Persist a valid native_redirect (+ PKCE challenge) from /auth; present-but-invalid clears stale, absent keeps the mode. */
export function captureNativeRedirect(params: URLSearchParams): void {
  const redirect = params.get(NATIVE_REDIRECT_PARAM);
  if (redirect === null) return;
  if (!isAllowedCallbackUrl(redirect)) {
    clearNativeRedirect();
    return;
  }
  const method = params.get(CODE_CHALLENGE_METHOD_PARAM) ?? "S256";
  const rawChallenge = params.get(CODE_CHALLENGE_PARAM);
  // Only keep an S256 challenge of the exact expected shape; anything else stores as "no challenge".
  const challenge =
    method === "S256" && rawChallenge !== null && CODE_CHALLENGE_RE.test(rawChallenge) ? rawChallenge : undefined;
  // Opaque, server-issued token: carry it as-is and let the backend's mint be the authority on validity.
  const launchNonce = params.get(LAUNCH_NONCE_PARAM) || undefined;
  const stored: { redirect: string; challenge?: string; launchNonce?: string } = { redirect };
  if (challenge) stored.challenge = challenge;
  if (launchNonce) stored.launchNonce = launchNonce;
  safeSessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
}

/** The stored, re-validated handoff (return URL + optional PKCE challenge), or null when not in native-handoff mode. */
export function getNativeHandoff(): NativeHandoff | null {
  const raw = safeSessionStorage.getItem(STORAGE_KEY);
  if (raw === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    clearNativeRedirect();
    return null;
  }
  // Treat any non-object shape (e.g. the string "null", which JSON.parse accepts) as tampering rather
  // than dereferencing it, so a poisoned value falls back to normal web login instead of throwing.
  if (typeof parsed !== "object" || parsed === null) {
    clearNativeRedirect();
    return null;
  }
  const stored = parsed as { redirect?: unknown; challenge?: unknown; launchNonce?: unknown };
  // Re-validate on read: never send the browser to a stored value without re-checking the allowlist.
  if (typeof stored.redirect !== "string" || !isAllowedCallbackUrl(stored.redirect)) {
    clearNativeRedirect();
    return null;
  }
  const handoff: NativeHandoff = { redirect: stored.redirect };
  if (typeof stored.challenge === "string" && CODE_CHALLENGE_RE.test(stored.challenge)) {
    handoff.challenge = stored.challenge;
    handoff.method = "S256";
  }
  if (typeof stored.launchNonce === "string" && stored.launchNonce.length > 0) {
    handoff.launchNonce = stored.launchNonce;
  }
  return handoff;
}

/** The stored app return URL, or null when not in native-handoff mode. */
export function getNativeRedirect(): string | null {
  return getNativeHandoff()?.redirect ?? null;
}

export function clearNativeRedirect(): void {
  safeSessionStorage.removeItem(STORAGE_KEY);
}

/** True while marketplace is running a system-browser native-handoff login. */
export function isNativeHandoffMode(): boolean {
  return getNativeRedirect() !== null;
}

type HandoffResult = { code: string } | { error: string };

/** Set the handoff outcome on the callback's query; base is a validated HTTPS URL, so the URL API applies. */
export function buildNativeRedirectUrl(base: string, result: HandoffResult): string {
  const [key, value] = "code" in result ? ["code", result.code] : ["error", result.error];
  const url = new URL(base);
  // set(), not append: a callback already carrying the key can't produce a duplicate the app misreads, and
  // URL serialization keeps the query ahead of any #fragment, so code/error never lands inside the fragment.
  url.searchParams.set(key, value);
  return url.toString();
}

/** In native mode, clear state and redirect the browser to the app with an error reason; returns whether it did. */
export function redirectToNativeError(reason: string): boolean {
  const redirect = getNativeRedirect();
  if (!redirect) return false;
  clearNativeRedirect();
  globalThis.location.assign(buildNativeRedirectUrl(redirect, { error: reason }));
  return true;
}

// A few seconds: this is awaited inside the shell middleware, so a longer budget makes an unreachable API
// freeze the user on the pre-navigation screen before the fail-closed redirect fires.
const MINT_TIMEOUT_MS = 6_000;

/** Mint a single-use code bound to the session, the PKCE challenge, and the launch nonce the backend verifies; no token in the URL. */
export async function mintNativeHandoffCode(
  challenge: string,
  method: CodeChallengeMethodEnum,
  launchNonce: string,
): Promise<string> {
  // Generated client sends the session cookie (credentials: "include") and throws on a non-2xx. Bound the wait
  // with a timeout signal, since this is awaited inside the shell middleware and a stalled mint hangs navigation.
  const { code } = await authNativeHandoffCodeCreate(
    { code_challenge: challenge, code_challenge_method: method, launch_nonce: launchNonce },
    { signal: AbortSignal.timeout(MINT_TIMEOUT_MS) },
  );
  if (!code) throw new Error("native-handoff code mint returned no code");
  return code;
}
